/**
 * プライバシーポリシー（ご利用者さま向け）＝**確定版の掲示**（T-D29・09 §K.3）。
 *
 * ★ **この画面が本文の正**（草案 `docs/legal/privacy-policy-workers-draft.md` は経緯と社内注記だけを残す）。
 *   2か所に同じ文章を置くと必ずずれるので、掲示している方を正とする。
 *
 * ★ **書いてあることは、今のシステムが実際にやっていることだけ**（09 §C・11 §2.2「効能を過大申告しない」）：
 *   - AI（Vertex AI）による下書き作成は **D-59 で凍結中＝現在は利用していない**。「使っています」と
 *     書くと事実と違ううえ、Vertex のデータ取扱条項（TBD-9・法務確認中）に依存した約束を
 *     先に置くことになる。導入するときに改定する、と書く。
 *   - FAX 画像の取込 UI も凍結中（T-D22）。電話・FAX・メールで受けた注文は**卸の担当者が
 *     内容を入力する**形＝原本の画像・本文を当社が預かる運用はまだ無い。よって
 *     インバウンドメール事業者（TBD-3）もサブプロセッサに載せない（使っていないものは書かない）。
 *   - 逆に、**職人プロフィール（お名前・電話番号・お届け先住所）は実際に集めている**（A-07・FR-18）
 *     のに草案の一覧に無かった＝一番重い項目なので明記した。
 *
 * ★ **〔要確定〕〔法務確認中〕の印は1つも残さない**（掲示物に社内メモを出さない）。
 */

const H2: React.CSSProperties = {
  fontSize: "var(--kz-font-size-lg)",
  margin: "var(--kz-space-5) 0 var(--kz-space-2)",
};
const P: React.CSSProperties = { margin: "0 0 var(--kz-space-3)", lineHeight: 1.9 };
const UL: React.CSSProperties = { margin: "0 0 var(--kz-space-3)", paddingLeft: "1.2em", lineHeight: 1.9 };

export function Privacy() {
  return (
    <main
      style={{
        padding: "var(--kz-space-5)",
        maxWidth: 720,
        margin: "0 auto",
        fontSize: "var(--kz-font-size-md)",
      }}
      data-testid="privacy"
    >
      <h1 style={{ fontSize: "var(--kz-font-size-xl)", margin: "0 0 var(--kz-space-2)" }}>
        建材オーダー プライバシーポリシー
      </h1>
      <p style={{ ...P, color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
        ご利用者さま（工務店・専門工事業者の皆さま）向け
      </p>

      <p style={P}>
        建材オーダー（以下「本サービス」）は、お取引先の建材卸売業者さまへのご注文を、LINE
        から簡単に行っていただくためのサービスです。
      </p>
      <p style={P}>
        株式会社PotentialX（以下「当社」）は、本サービスをご利用いただくにあたり取得するお客様（以下
        「利用者」）の個人情報を、以下のとおり取り扱います。本サービスの提供元は当社のみです。
      </p>

      <h2 style={H2}>1. 取得する個人情報と取得方法</h2>
      <ul style={UL}>
        <li>
          <strong>LINE のプロフィール情報</strong>（表示名・プロフィール画像）と、LINE
          が発行する利用者ごとの識別子 — LINE の公式アカウントを友だち追加いただいたときに取得します。識別子は画面には表示されません。
        </li>
        <li>
          <strong>お名前・電話番号・既定のお届け先住所</strong> —
          アプリの「プロフィール」画面で、利用者ご自身にご入力いただいた場合に取得します（任意）。ご注文の連絡や配送のために、お取引先の卸売業者さまに表示されます。
        </li>
        <li>
          <strong>ご所属</strong>（会社名・屋号） — 利用者による任意のご入力。
        </li>
        <li>
          <strong>ご注文の内容</strong>（商品・数量・お届け先の現場・希望日・備考など） —
          アプリでのご注文操作、または電話・FAX・メールでお受けしたご注文を卸売業者さまの担当者が入力したもの。
        </li>
        <li>
          <strong>接続・利用の記録</strong>（接続元 IP アドレス・ブラウザ情報・操作の記録） —
          安全の確保のため、ご利用時に自動的に取得します。
        </li>
      </ul>
      <p style={P}>
        本サービスは、<strong>お取引先の建材卸売業者さまから発行される招待コード</strong>
        を通じてご利用を開始いただく仕組みです（LINE の友だち追加 → 招待コードの入力 → 接続）。
      </p>

      <h2 style={H2}>2. 利用目的</h2>
      <ul style={UL}>
        <li>受発注の処理 — ご注文を、お取引先の建材卸売業者さまへ正しくお伝えするため。</li>
        <li>
          進捗のお知らせ — 受付・確定・出荷・配送・納品などの状況を LINE の通知でお届けするため。
        </li>
        <li>接続の管理 — 招待コードによる接続、なりすましや不正利用の防止のため。</li>
        <li>品質改善・お問い合わせ対応 — サービスの品質向上、障害対応、お問い合わせへの回答のため。</li>
      </ul>

      <h2 style={H2}>3. AI による処理について</h2>
      <p style={P}>
        <strong>現在、本サービスは AI による処理を利用していません。</strong>
        将来、ご注文の下書き作成などに AI を利用する場合は、利用する事業者・処理される情報の範囲・
        データの所在地を本ポリシーに明記して改定し、あらかじめお知らせします。
      </p>
      <p style={P}>
        なお、ご注文の内容を最終的に確定するのは、いかなる場合も
        <strong>お取引先の卸売業者さまの担当者（人）</strong>です。ご注文が自動で確定することはありません。
      </p>

      <h2 style={H2}>4. 第三者への提供・業務の委託</h2>
      <h3 style={{ ...H2, fontSize: "var(--kz-font-size-md)" }}>4.1 お取引先の建材卸売業者さまへの伝達</h3>
      <p style={P}>
        利用者のご注文内容は、<strong>そのご注文をお受けする建材卸売業者さま</strong>
        に伝達されます。これは、利用者がその卸売業者さまにご注文いただくために本サービスをお使いいただくものであり、サービスの本旨に基づく取扱いです。伝達された後の取引データについては、当該卸売業者さまが取扱いの主体となり、当社はその委託を受けて取り扱います。
      </p>
      <h3 style={{ ...H2, fontSize: "var(--kz-font-size-md)" }}>4.2 業務の委託先</h3>
      <p style={P}>当社は、本サービスの運営のために次の事業者へ業務の一部を委託し、適切に監督します。</p>
      <ul style={UL}>
        <li>
          <strong>Google LLC（Google Cloud）</strong> — サーバー・データベース・ファイルの保管。
          データの所在地は<strong>日本国内（東京）</strong>です。
        </li>
        <li>
          <strong>LINEヤフー株式会社</strong>（LINE Messaging API・LINE Login／LIFF） —
          友だち追加・接続・メッセージの配送。データの所在地は LINE プラットフォームの管理によります。
        </li>
      </ul>
      <p style={P}>
        委託先を追加・変更する場合は、あらかじめお知らせします。上記の目的以外で、利用者の個人情報を第三者に提供することはありません（法令に基づく場合を除きます）。
      </p>

      <h2 style={H2}>5. 保管とセキュリティ</h2>
      <ul style={UL}>
        <li>
          <strong>暗号化</strong> — お名前・電話番号・お届け先住所などは暗号化して保管します。通信はすべて暗号化しています。
        </li>
        <li>
          <strong>アクセスの制限</strong> —
          当社の担当者が実名などを閲覧できる範囲は既定で制限しており、必要な場合に限り、記録を残したうえで閲覧します。
        </li>
        <li>
          <strong>保管場所</strong> — データは日本国内（東京）の設備に保管します。
        </li>
        <li>
          <strong>ログの取扱い</strong> — 障害対応などのための技術的なログには、氏名などの個人情報を出力しない運用としています。
        </li>
      </ul>
      <p style={{ ...P, color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
        ※ 本サービスは不正アクセス等に対し合理的な安全管理措置を講じますが、インターネットを通じた情報の送受信について完全な安全性を保証するものではありません。
      </p>

      <h2 style={H2}>6. 保存期間</h2>
      <ul style={UL}>
        <li>ご注文・接続などの利用データ — ご利用が続く間、および取引・帳簿上必要な期間。</li>
        <li>LINE の受信イベントの記録・接続元 IP・ブラウザ情報 — 約 90 日。</li>
        <li>
          お取引先の卸売業者さまが本サービスの利用を終了した場合 — 終了後
          <strong>90 日間</strong>保持し、その後に削除します（削除前に、必要なデータのお引き渡しを行います）。
        </li>
      </ul>
      <p style={{ ...P, color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
        ※ 削除後も、バックアップの保持期間（最長で数日程度）の間はバックアップ内にデータが残り、期間の経過とともに消去されます。
      </p>

      <h2 style={H2}>7. 開示・訂正・利用停止・削除のご請求</h2>
      <p style={P}>
        利用者は、当社が保有するご自身の個人情報について、開示・訂正・追加・削除・利用停止をご請求いただけます。第
        9 項のお問い合わせ窓口までご連絡ください。ご本人であることを確認のうえ、法令に従って対応します。
      </p>
      <p style={P}>
        お取引先の卸売業者さまが管理する取引データに関するご請求については、当該卸売業者さまが窓口となります。当社は、その指示に基づき対応します。
      </p>

      <h2 style={H2}>8. LINE 連携について</h2>
      <ul style={UL}>
        <li>
          <strong>取得する範囲</strong> —
          友だち追加時に、LINE の表示名・プロフィール画像・利用者ごとの識別子を取得します。利用者を特定して正しく通知をお届けし、お取引先との接続を管理するために使います。
        </li>
        <li>
          <strong>通知の範囲</strong> —
          当社が LINE でお送りするのは、ご注文への応答や、接続済みのお取引に関する業務上のお知らせに限られます。広告目的の一方的なメッセージはお送りしません。
        </li>
        <li>
          <strong>友だち解除（ブロック）した場合</strong> —
          友だち解除をされると、当社は該当アカウントを「ブロック中」として扱います。
          <strong>再度友だち追加をされても、お取引先との接続は自動では元に戻りません</strong>
          （再開には、お取引先の卸売業者さまへのご依頼が必要です）。
        </li>
      </ul>

      <h2 style={H2}>9. お問い合わせ窓口</h2>
      <ul style={UL}>
        <li>事業者名：株式会社PotentialX</li>
        <li>所在地：東京都渋谷区恵比寿2丁目28番地10号 ベンチャーシティ恵比寿3265</li>
        <li>
          お問い合わせ先：
          <a href="mailto:support@potential-x.com" style={{ color: "var(--kz-color-primary)" }}>
            support@potential-x.com
          </a>
        </li>
        <li>個人情報の取扱いに関する責任者：当社サービス運営責任者（上記のお問い合わせ先までご連絡ください）</li>
      </ul>

      <h2 style={H2}>10. 本ポリシーの改定</h2>
      <p style={P}>
        当社は、法令の変更やサービス内容の変更に応じて、本ポリシーを改定することがあります。重要な変更を行う場合は、本サービス内での掲示等により事前にお知らせします。
      </p>
      <p style={{ ...P, color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
        制定日：2026年8月1日
      </p>
    </main>
  );
}
